import React, { useState, useEffect, useCallback } from 'react';
import './Admin.css';
import ConfirmModal from '../ConfirmModal';
import { API_BASE_URL } from '../../utils/api';

// The Admin tab.
//
// Two lists, because there are two different situations and mixing them up is
// what makes permission screens confusing:
//
//   Users       - people who already have an account. Tick the applications
//                 they may open; it takes effect immediately.
//   Invitations - people with no account yet. The applications chosen here are
//                 parked against their email and handed over the first time
//                 they sign in with Google.
//
// Nothing on this screen is the real security boundary. Every call below is
// refused by the server unless the caller's stored role is 'admin', so showing
// this tab to someone who edits their own localStorage gains them nothing.

function Admin() {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', firstName: '', lastName: '', applicationIds: [] });
  const [inviting, setInviting] = useState(false);
  // Access is edited in a dialog rather than inline. A row of tick boxes is
  // fine for five applications and unreadable at twenty - the row grows
  // sideways and the table stops being scannable.
  const [accessFor, setAccessFor] = useState(null);     // the user being edited
  const [accessSelection, setAccessSelection] = useState([]);
  const [accessQuery, setAccessQuery] = useState('');
  // Removing a user and cancelling an invitation both go through the app's own
  // ConfirmModal, the same one the logout button uses - window.confirm draws
  // the browser's grey box, which looks nothing like the rest of TestFlow.
  const [confirmAction, setConfirmAction] = useState(null);

  // Dismissible, and on screen long enough to actually be read - an invite
  // reply can be two sentences about whether the email went out.
  const flashSuccess = (message) => {
    setSuccess(message);
    setError(null);
    setTimeout(() => setSuccess(null), 8000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersResponse, invitesResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/users`),
        fetch(`${API_BASE_URL}/api/admin/invitations`),
      ]);

      const usersData = await usersResponse.json();
      const invitesData = await invitesResponse.json();

      if (!usersData.success) throw new Error(usersData.error || 'Could not load users');

      setUsers(usersData.users || []);
      setApplications(usersData.applications || []);
      setInvitations(invitesData.success ? (invitesData.invitations || []) : []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not load the admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAccessDialog = (user) => {
    setAccessFor(user);
    setAccessSelection(user.applicationIds);
    setAccessQuery('');
    setError(null);
  };

  const closeAccessDialog = () => {
    setAccessFor(null);
    setAccessSelection([]);
    setAccessQuery('');
  };

  const toggleAccessSelection = (applicationId) => {
    setAccessSelection((current) => current.includes(applicationId)
      ? current.filter((id) => id !== applicationId)
      : [...current, applicationId]);
  };

  // One save for the whole set, so a user's access is never half-applied.
  const saveAccess = async () => {
    if (!accessFor) return;

    setSavingId(accessFor.id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${accessFor.id}/applications`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationIds: accessSelection }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Could not save');

      setUsers((current) => current.map((row) =>
        row.id === accessFor.id ? { ...row, applicationIds: data.applicationIds } : row));
      flashSuccess(`Access updated for ${accessFor.email}`);
      closeAccessDialog();
    } catch (err) {
      setError(err.message || 'Could not update access');
    } finally {
      setSavingId(null);
    }
  };

  const removeUser = async (user) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${user.id}`, { method: 'DELETE' });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Could not remove that user');
      setUsers((current) => current.filter((row) => row.id !== user.id));
      flashSuccess(`${user.email} removed`);
    } catch (err) {
      setError(err.message || 'Could not remove that user');
    }
  };

  const submitInvite = async () => {
    if (!inviteForm.email.trim()) { setError('Enter an email address'); return; }
    if (!inviteForm.applicationIds.length) {
      setError('Choose at least one application, or they will sign in to an empty screen');
      return;
    }

    setInviting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inviteForm),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Could not send that invitation');

      const granted = data.applicationCount ?? inviteForm.applicationIds.length;
      setShowInvite(false);
      setInviteForm({ email: '', firstName: '', lastName: '', applicationIds: [] });
      flashSuccess(
        `${inviteForm.email} invited with access to ${granted} application${granted === 1 ? '' : 's'}. `
        + (data.message || '')
      );
      load();
    } catch (err) {
      setError(err.message || 'Could not send that invitation');
    } finally {
      setInviting(false);
    }
  };

  const cancelInvitation = async (invitation) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/invitations/${encodeURIComponent(invitation.email)}`,
        { method: 'DELETE' }
      );
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Could not cancel that invitation');
      setInvitations((current) => current.filter((row) => row.email !== invitation.email));
      flashSuccess(`Invitation for ${invitation.email} cancelled`);
    } catch (err) {
      setError(err.message || 'Could not cancel that invitation');
    }
  };

  const toggleInviteApplication = (applicationId) => {
    setInviteForm((form) => ({
      ...form,
      applicationIds: form.applicationIds.includes(applicationId)
        ? form.applicationIds.filter((id) => id !== applicationId)
        : [...form.applicationIds, applicationId],
    }));
  };

  // Either dialog being open moves error reporting inside it.
  const dialogOpen = showInvite || !!accessFor;

  const openInvite = () => {
    setError(null);
    setShowInvite(true);
  };

  const closeInvite = () => {
    setError(null);
    setShowInvite(false);
  };

  const displayName = (user) => {
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    return String(user.email || '').split('@')[0];
  };

  const initials = (user) => {
    const name = displayName(user);
    const parts = name.split(/[.\s_-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <h1>Admin</h1>
          <p className="admin-subtitle">
            Who may use TestFlow, and which applications each person can open.
          </p>
        </div>
        <button className="admin-btn-primary" onClick={openInvite}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Invite User
        </button>
      </div>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${tab === 'users' ? 'active' : ''}`}
          onClick={() => setTab('users')}
        >
          Users <span className="admin-count">{users.length}</span>
        </button>
        <button
          className={`admin-tab ${tab === 'invitations' ? 'active' : ''}`}
          onClick={() => setTab('invitations')}
        >
          Invitations <span className="admin-count">{invitations.length}</span>
        </button>
      </div>

      {success && (
        <div className="admin-message success">
          {success}
          <button onClick={() => setSuccess(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      {/* An error raised by a dialog is shown INSIDE that dialog. Rendered
          here it would sit in the page behind the overlay, half hidden by the
          dialog that caused it. */}
      {error && !dialogOpen && (
        <div className="admin-message error">
          {error}
          <button onClick={() => setError(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {loading ? (
        <div className="admin-empty">Loading…</div>
      ) : tab === 'users' ? (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>Application access</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className={savingId === user.id ? 'saving' : ''}>
                  <td>
                    <div className="admin-person">
                      <div className="admin-avatar">
                        {user.avatarUrl
                          ? <img src={user.avatarUrl} alt="" />
                          : <span>{initials(user)}</span>}
                      </div>
                      <div>
                        <div className="admin-person-name">{displayName(user)}</div>
                        <div className="admin-person-email">{user.email}</div>
                        {!user.withinAllowedDomain && (
                          <span className="admin-flag" title="Outside the allowed email domains">
                            outside allowed domain
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`admin-role ${user.role}`}>{user.role}</span>
                  </td>
                  <td>
                    {user.role === 'admin' ? (
                      <span className="admin-all-apps">All applications</span>
                    ) : (
                      <div className="admin-access-cell">
                        <span className="admin-access-count">
                          <strong>{user.applicationIds.length}</strong> of {applications.length}
                        </span>
                        {user.applicationIds.length === 0 ? (
                          <span className="admin-flag">no access</span>
                        ) : (
                          <span className="admin-access-names">
                            {applications
                              .filter((app) => user.applicationIds.includes(app.id))
                              .slice(0, 2)
                              .map((app) => app.name)
                              .join(', ')}
                            {user.applicationIds.length > 2 &&
                              ` +${user.applicationIds.length - 2} more`}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="admin-actions">
                    {user.role !== 'admin' && (
                      <button className="admin-btn-secondary" onClick={() => openAccessDialog(user)}>
                        Manage access
                      </button>
                    )}
                    {!user.isSeededAdmin && (
                      <button
                        className="admin-btn-danger"
                        onClick={() => setConfirmAction({
                          title: 'Remove user',
                          message: `Remove ${user.email}? They will be signed out and lose access. `
                            + 'Their test cases and runs are not deleted — those belong to the application.',
                          confirmText: 'Remove user',
                          run: () => removeUser(user),
                        })}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : invitations.length === 0 ? (
        <div className="admin-empty">
          No invitations yet. Use <strong>Invite User</strong> to add someone.
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Invited</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {invitations.map((invitation) => (
                <tr key={invitation.email}>
                  <td className="admin-person-email">{invitation.email}</td>
                  <td className="admin-muted">
                    {invitation.invitedAt
                      ? new Date(invitation.invitedAt).toLocaleDateString()
                      : '—'}
                  </td>
                  <td>
                    {/* 'status' is the real signal, not whether a users row
                        exists - the invite flow creates that row immediately,
                        so it would say "signed in" the moment you invited
                        someone. team_members.status only flips to 'active'
                        when they actually sign in. */}
                    <span className={`admin-status ${invitation.status === 'active' ? 'active' : 'pending'}`}>
                      {invitation.status === 'active' ? 'signed in' : 'not signed in yet'}
                    </span>
                  </td>
                  <td className="admin-actions">
                    <button
                      className="admin-btn-danger"
                      onClick={() => setConfirmAction({
                        title: 'Cancel invitation',
                        message: `Cancel the invitation for ${invitation.email}? `
                          + 'They will not be able to sign in until you invite them again.',
                        confirmText: 'Cancel invitation',
                        run: () => cancelInvitation(invitation),
                      })}
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmAction}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmText={confirmAction?.confirmText}
        cancelText="Keep"
        type="danger"
        onConfirm={() => {
          const action = confirmAction;
          setConfirmAction(null);
          action?.run();
        }}
        onCancel={() => setConfirmAction(null)}
      />

      {accessFor && (
        <div className="admin-modal-overlay" onClick={closeAccessDialog}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div>
                <h3>Application access</h3>
                <p className="admin-modal-sub">{accessFor.email}</p>
              </div>
              <button className="admin-modal-close" onClick={closeAccessDialog}>×</button>
            </div>

            <div className="admin-modal-body">
              {error && (
                <div className="admin-message error inline">
                  {error}
                  <button onClick={() => setError(null)} aria-label="Dismiss">×</button>
                </div>
              )}
              {/* Only worth showing once the list is long enough to need it. */}
              {applications.length > 6 && (
                <input
                  type="text"
                  className="admin-search"
                  placeholder="Search applications…"
                  value={accessQuery}
                  onChange={(e) => setAccessQuery(e.target.value)}
                  autoFocus
                />
              )}

              <div className="admin-picker-toolbar">
                <span className="admin-picker-count">
                  {accessSelection.length} of {applications.length} selected
                </span>
                <div>
                  <button
                    type="button"
                    className="admin-link-btn"
                    onClick={() => setAccessSelection(applications.map((app) => app.id))}
                  >
                    Select all
                  </button>
                  <button className="admin-link-btn" onClick={() => setAccessSelection([])}>
                    Clear all
                  </button>
                </div>
              </div>

              <div className="admin-picker-list">
                {applications
                  .filter((app) => app.name.toLowerCase().includes(accessQuery.trim().toLowerCase()))
                  .map((app) => (
                    <label key={app.id} className="admin-picker-row">
                      <input
                        type="checkbox"
                        checked={accessSelection.includes(app.id)}
                        onChange={() => toggleAccessSelection(app.id)}
                      />
                      <span>{app.name}</span>
                    </label>
                  ))}
                {applications.filter((app) =>
                  app.name.toLowerCase().includes(accessQuery.trim().toLowerCase())).length === 0 && (
                  <div className="admin-picker-empty">No application matches that.</div>
                )}
              </div>

              {accessSelection.length === 0 && (
                <p className="admin-hint warn">
                  With nothing selected they will sign in to an empty screen.
                </p>
              )}
            </div>

            <div className="admin-modal-footer">
              <button className="admin-btn-ghost" onClick={closeAccessDialog}>Cancel</button>
              <button
                className="admin-btn-primary"
                onClick={saveAccess}
                disabled={savingId === accessFor.id}
              >
                {savingId === accessFor.id ? 'Saving…' : 'Save access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvite && (
        <div className="admin-modal-overlay" onClick={closeInvite}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Invite User</h3>
              <button className="admin-modal-close" onClick={closeInvite}>×</button>
            </div>
            <div className="admin-modal-body">
              {error && (
                <div className="admin-message error inline">
                  {error}
                  <button onClick={() => setError(null)} aria-label="Dismiss">×</button>
                </div>
              )}
              <div className="admin-field">
                <label htmlFor="invite-email">Work email *</label>
                <input
                  id="invite-email"
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="name@7edge.com"
                />
              </div>
              <div className="admin-field-row">
                <div className="admin-field">
                  <label htmlFor="invite-first">First name</label>
                  <input
                    id="invite-first"
                    type="text"
                    value={inviteForm.firstName}
                    onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                  />
                </div>
                <div className="admin-field">
                  <label htmlFor="invite-last">Last name</label>
                  <input
                    id="invite-last"
                    type="text"
                    value={inviteForm.lastName}
                    onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="admin-field">
                <label>Applications they may open *</label>
                <div className="admin-picker-toolbar">
                  <span className="admin-picker-count">
                    {inviteForm.applicationIds.length} of {applications.length} selected
                  </span>
                  <div>
                    <button
                      type="button"
                    className="admin-link-btn"
                      onClick={() => setInviteForm((form) => ({
                        ...form, applicationIds: applications.map((app) => app.id),
                      }))}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                    className="admin-link-btn"
                      onClick={() => setInviteForm((form) => ({ ...form, applicationIds: [] }))}
                    >
                      Clear all
                    </button>
                  </div>
                </div>
                <div className="admin-picker-list">
                  {applications.map((app) => (
                    <label key={app.id} className="admin-picker-row">
                      <input
                        type="checkbox"
                        checked={inviteForm.applicationIds.includes(app.id)}
                        onChange={() => toggleInviteApplication(app.id)}
                      />
                      <span>{app.name}</span>
                    </label>
                  ))}
                </div>
                <p className="admin-hint">
                  They will see only these. You can change it any time from the Users tab.
                </p>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn-ghost" onClick={closeInvite}>Cancel</button>
              <button className="admin-btn-primary" onClick={submitInvite} disabled={inviting}>
                {inviting ? 'Sending…' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
